/**
 * @file StockHintLine.tsx
 * @description Колонка/строка «Откуда брать» в задании на сборку
 * (phase19.24 §3.1, п. 0.4.3 фазы).
 *
 * До этого строка без резерва молчала о месте и остатке: кладовщик видел
 * только «без резерва» и шёл искать товар наугад. Теперь ответ всегда есть —
 * реестр формулировок, а не каскад `if`.
 *
 * Остаток и доступность — разные вещи, и действия по ним разные (замечание
 * ревью phase19.24 этап 0): «товара нет» ищут на приёмке, «всё занято под
 * другие заказы» разбирают с менеджером по броням.
 *
 * | Ситуация | Первая строка | Вторая строка | Тон |
 * | --- | --- | --- | --- |
 * | есть резерв | ячейка | партия · до | обычный |
 * | `stockOnHand = 0` | «Остатка нет» | «ячейка A-01» / «ячейка не назначена» | destructive |
 * | остаток есть, доступно 0 | ячейка | «Занято под другие заказы · на складе N» | amber |
 * | доступно > 0, брони нет | ячейка | «Резерва нет · доступно N» | amber |
 *
 * Один и тот же блок стоит в таблице на `lg` и в карточке на `<lg` — иначе
 * телефон и десктоп рассказывали бы о товаре разное.
 */

import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  formatLotExpiry,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { formatQtyValue, pickUnit } from "../../../lib/picking-format";

/** Тон подсказки: спокойный, предупреждение, блокирующая проблема. */
type HintTone = "default" | "warning" | "danger";

interface Hint {
  tone: HintTone;
  /** Главная строка: адрес ячейки либо «Остатка нет». */
  place: string;
  /** Пояснение: партия и срок, остаток, «ячейка не назначена». */
  note: string | null;
  /** Первая строка — адрес ячейки: печатаем моноширинным. */
  mono: boolean;
  /** Подсказка по наведению — что делать дальше. */
  title?: string;
}

const TONE_CLASS: Record<HintTone, string> = {
  default: "text-on-surface",
  warning: "text-warning",
  danger: "text-destructive",
};

/** Реестр формулировок §3.1: решение принимается один раз и в одном месте. */
function resolveStockHint(item: PickingTaskItemDto): Hint {
  // Единица — из самой строки: «на складе 400 г» и «на складе 400 шт» —
  // разные сообщения, а раньше подсказка всегда писала «шт».
  const unit = pickUnit(item.unit);
  const lot = item.pickedLot ?? item.plannedLot;
  const expiry = formatLotExpiry(lot);
  const bin = item.binCode ?? item.defaultBinCode;
  const onHand = Number(item.stockOnHand);
  const available = Number(item.stockAvailable);

  // Строка с бронью: собирать точно есть что — показываем адрес и партию.
  // `!== false`: поле опционально (старый сервер), и «неизвестно» тут значит
  // «бронь есть» — так работало до phase19.23.
  if (item.hasReservation !== false) {
    return {
      tone: "default",
      place: bin ?? "Ячейка не назначена",
      note: lot ? `${lot.batchNumber}${expiry ? ` · ${expiry}` : ""}` : null,
      mono: Boolean(bin),
    };
  }

  // Физически пусто: ячейку всё равно называем — кладовщик проверит полку
  // глазами, прежде чем оформлять недостачу.
  if (onHand <= 0) {
    return {
      tone: "danger",
      place: "Остатка нет",
      note: bin ? `ячейка ${bin}` : "ячейка не назначена",
      mono: false,
      title: "Проверьте приёмку или оформите недостачу",
    };
  }

  // Товар на полке лежит, но весь занят чужими заказами: брать его — значит
  // сорвать другой заказ, решение принимает менеджер.
  if (available <= 0) {
    return {
      tone: "warning",
      place: bin ?? "Ячейка не назначена",
      // Decimal с сервера печатаем через formatQtyValue: «на складе 10.000 шт»
      // кладовщик читал как «десять тысяч» (QA phase19.24 этап 0).
      note: `Занято под другие заказы · на складе ${formatQtyValue(item.stockOnHand)} ${unit}`,
      mono: Boolean(bin),
      title:
        "Весь остаток забронирован под другие заказы — проверьте брони или оформите недостачу",
    };
  }

  // Брони нет, но свободный остаток есть — спишется в момент проведения.
  return {
    tone: "warning",
    place: bin ?? "Ячейка не назначена",
    note: `Резерва нет · доступно ${formatQtyValue(item.stockAvailable)} ${unit}`,
    mono: Boolean(bin),
    title: "Товар не забронирован — спишется при проведении сборки",
  };
}

interface Props {
  item: PickingTaskItemDto;
  className?: string;
}

export function StockHintLine({ item, className }: Props) {
  const hint = resolveStockHint(item);
  return (
    <span
      className={cn("flex min-w-0 flex-col gap-0.5 text-sm", className)}
      title={hint.title}
    >
      <span className={cn("flex items-center gap-1.5", TONE_CLASS[hint.tone])}>
        {hint.tone === "danger" ? (
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
        ) : null}
        <span className={hint.mono ? "font-mono" : ""}>{hint.place}</span>
      </span>
      {hint.note ? (
        <span
          className={cn(
            "text-xs",
            hint.tone === "default" ? "text-on-surface-variant" : TONE_CLASS[hint.tone],
          )}
        >
          {hint.note}
        </span>
      ) : null}
    </span>
  );
}
